import { logError } from '@com-delta-idp/mnx-utils';
import {
  Notification,
  NotificationType
} from '../models/notification.interfaces';
import { findElement } from '../utils';

/**
 * Generates a notification object based on the provided parameters.
 *
 * @param type - The type of notification (success or error).
 * @param message - The message to display in the notification.
 * @param errors - Optional. The error message with a count or not.
 * @param focusItem - The element or ID/class to focus on if an error occurs.
 *
 * @returns A Notification object containing the notification details.
 *
 * @throws Logs an error if the focus item is not found in the DOM.
 *
 * @example
 * Success: getNotification('Operation successful', 'success');
 *
 * ** Inferred focusItem type **
 * - getNotification('error', 'To continue, please correct the ', '6 items indicated.', 'myElementId');
 * - getNotification('error', 'To continue, please correct the ', '6 items indicated.', element);
 *
 * ** Specific focusItem type **
 * - getNotification<string>('error', 'To continue, please correct the ', '6 items indicated.', 'myElementId');
 * - getNotification<Element>('error', 'To continue, please correct the ', '6 items indicated.', element);
 */
export const getNotification = <T extends string | Element>(
  type: NotificationType,
  message: string,
  errors = '',
  focusItem?: T
): Notification => {
  const element: Element | null = getElement(focusItem);

  if (shouldLogError(type, element)) {
    logErrorDetails(focusItem);
  }

  return adaptNotification(type, message, errors, element);
};

const shouldLogError = (
  type: NotificationType,
  element: Element | null
): boolean => {
  return !element && type === 'error';
};

const logErrorDetails = <T extends string | Element>(focusItem?: T) => {
  const adaptedError = adaptError(focusItem ?? null);
  const itemDetails = getItemDetails(focusItem ?? null);

  logError(adaptedError, 'Notification', 'Shared', itemDetails);
};

const getElement = <T extends string | Element>(
  focusItem?: T
): Element | null => {
  return typeof focusItem === 'string'
    ? findElement(focusItem)
    : focusItem ?? null;
};

const adaptError = (focusItem: string | Element | null): Error => ({
  message: `Element not found: ${focusItem}`,
  name: '[UI Error]',
  stack: getItemDetails(focusItem)
});

const adaptNotification = (
  type: NotificationType,
  message: string,
  errors: string,
  focusItem: Element | null
): Notification => {
  if (type === 'error') {
    return {
      type,
      message,
      errors,
      focusItem
    };
  }

  return {
    type,
    message
  };
};

const getItemDetails = (focusItem: Element | string | null): string =>
  focusItem instanceof Element
    ? formatElementDetails(focusItem)
    : typeof focusItem === 'string'
    ? `String: ${focusItem}`
    : 'No focus item available.';

const formatElementDetails = (element: Element): string =>
  `Element: <${element.tagName.toLowerCase()}>${
    element.id ? ` ID: ${element.id}` : ''
  }${element.className ? ` Classes: ${element.className}` : ''}`.trim();
